// components/navbar/MobileNavbar.js
import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import NavLogo from './NavLogo';
import NavLinks from './NavLinks';
import ThemeToggle from './ThemeToggle';
import NavSearch from './NavSearch';
import NavAuth from './NavAuth';

const MobileNavbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const navbarRef = useRef(null);
  const menuRef = useRef(null);
  const location = useLocation();

  // Track search expanded state
  const handleSearchExpandedChange = (expanded) => {
    setIsSearchExpanded(expanded);
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click was on the backdrop or outside both navbar and menu
      if (
        isMenuOpen &&
        navbarRef.current && 
        !navbarRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !event.target.closest('.menu-toggle') &&
        !event.target.closest('.mobile-nav-item') &&
        !event.target.closest('.mobile-dropdown-item')
      ) {
        setIsMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Close menu handler
  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Stop propagation for clicks inside the menu
  const handleMenuClick = (e) => {
    // Don't stop propagation for NavLink clicks, but prevent backdrop click
    const isNavLink = 
      e.target.classList.contains('mobile-nav-item') || 
      e.target.classList.contains('mobile-dropdown-item');
      
    if (!isNavLink) {
      e.stopPropagation();
    }
  };

  return (
    <>
      <nav className={`navbar mobile-navbar ${isSearchExpanded ? 'search-expanded' : ''}`} ref={navbarRef}>
        <div className="navbar-container">
          {/* Conditionally render logo */}
          {!isSearchExpanded && <NavLogo />}

          {/* Conditionally render menu toggle */}
          {!isSearchExpanded && (
            <button
              className="menu-toggle"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
            >
              <div className={`hamburger ${isMenuOpen ? "open" : ""}`}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </button>
          )}

          <div className="navbar-right">
            <NavSearch onExpandedChange={handleSearchExpandedChange} />
          </div>
        </div>
      </nav>
      
      {/* Mobile Menu */}
      <div 
        className={`mobile-menu ${isMenuOpen ? 'active' : ''}`} 
        onClick={handleMenuClick}
        ref={menuRef}
      >
        <div className="mobile-menu-content">
          <div className="mobile-nav-links">
            <NavLinks isMobile={true} closeMenu={closeMenu} />
          </div>
          
          <div className="mobile-menu-bottom">
            <div className="mobile-theme-toggle">
              <ThemeToggle />
            </div>
            
            <NavAuth isMobile={true} />
          </div>
        </div>
      </div>
      
      {/* Mobile menu backdrop */}
      <div 
        className={`menu-backdrop ${isMenuOpen ? 'active' : ''}`}
        onClick={closeMenu}
      />
    </>
  );
};

export default MobileNavbar;